import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Users, CheckCircle2, Circle, Star } from "lucide-react";

export default function PortalJobs() {
  const [missions, setMissions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [detail, setDetail] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/portal/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/portal/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const openDetail = async (id) => {
    setReviewForm({ rating: 0, comment: "" });
    const { data } = await api.get(`/portal/missions/${id}`);
    setDetail(data);
  };

  const submitReview = async (rev) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/portal/reviews/${rev.id}`, reviewForm);
      setReviews((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      toast.success("Thanks for your review!");
    } catch (e) { toast.error(errMsg(e)); }
    setSubmitting(false);
  };

  return (
    <div data-testid="portal-jobs-page">
      <h1 className="font-heading font-bold text-2xl mb-6">My Jobs</h1>
      <div className="grid md:grid-cols-2 gap-4">
        {missions.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
        {missions.map((m) => {
          const done = m.checklist?.filter((c) => c.done).length || 0;
          const pct = m.checklist?.length ? Math.round((done / m.checklist.length) * 100) : 0;
          return (
            <button key={m.id} data-testid={`portal-job-card-${m.mission_number}`} onClick={() => openDetail(m.id)}
              className="text-left rounded-xl border bg-card p-6 card-lift">
              <div className="flex items-center justify-between">
                <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number}</p>
                <StatusBadge status={m.status} />
              </div>
              <p className="font-heading font-semibold text-lg mt-2">{serviceName(m.service_type)}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtDate(m.scheduled_date)} · {m.scheduled_time}</p>
              <div className="mt-4">
                <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5 font-mono-cc"><span>PROGRESS</span><span>{pct}%</span></div>
                <Progress value={pct} className="h-1.5" />
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="portal-job-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono-cc text-sm text-[var(--cc-accent)]">{detail.mission_number}</span>
                  {serviceName(detail.service_type)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 text-sm">
                <div className="flex items-center justify-between">
                  <StatusBadge status={detail.status} />
                  <span className="text-muted-foreground text-xs">{fmtDate(detail.scheduled_date)} · {detail.scheduled_time}</span>
                </div>
                <div className="space-y-2">
                  {detail.pickup_address && <p className="flex gap-2"><MapPin className="w-4 h-4 text-[var(--cc-accent)] shrink-0 mt-0.5" /> <span><span className="text-muted-foreground">Pickup:</span> {detail.pickup_address}</span></p>}
                  {detail.dropoff_address && <p className="flex gap-2"><MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> <span><span className="text-muted-foreground">Dropoff:</span> {detail.dropoff_address}</span></p>}
                </div>
                {detail.crew_details?.length > 0 && (
                  <div>
                    <p className="overline-cc mb-2">Your Crew</p>
                    {detail.crew_details.map((c) => (
                      <p key={c.name} className="flex items-center gap-2 py-1"><Users className="w-4 h-4 text-muted-foreground" /> {c.name} <span className="text-xs text-muted-foreground">— {c.role}</span></p>
                    ))}
                  </div>
                )}
                {detail.photos?.length > 0 && (
                  <div data-testid="portal-job-photos">
                    <p className="overline-cc mb-2">Job Photos</p>
                    <div className="grid grid-cols-3 gap-2">
                      {detail.photos.map((p) => (
                        <div key={p.id} className="relative rounded-lg overflow-hidden border aspect-square">
                          <img src={p.photo} alt={p.kind} className="w-full h-full object-cover" />
                          <span className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-mono-cc font-bold uppercase ${p.kind === "before" ? "bg-amber-400 text-black" : "bg-emerald-500 text-black"}`}>{p.kind}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="overline-cc mb-2">Checklist</p>
                  {detail.checklist?.map((c, i) => (
                    <p key={i} className="flex items-center gap-2 py-1">
                      {c.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
                      <span className={c.done ? "" : "text-muted-foreground"}>{c.item}</span>
                    </p>
                  ))}
                </div>
                <div>
                  <p className="overline-cc mb-2">Timeline</p>
                  <div className="space-y-3 border-l-2 border-border pl-4">
                    {detail.timeline?.slice().reverse().map((t, i) => (
                      <div key={i}>
                        <p className="text-sm">{t.label}</p>
                        <p className="text-[11px] text-muted-foreground font-mono-cc">{new Date(t.ts).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {(() => {
                  const rev = reviews.find((r) => r.mission_id === detail.id);
                  if (!rev) return null;
                  if (rev.status === "submitted")
                    return (
                      <div className="rounded-xl border bg-secondary/40 p-4" data-testid="portal-review-submitted">
                        <p className="overline-cc mb-2">Your Review</p>
                        <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= rev.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}`} />)}</div>
                        {rev.comment && <p className="text-xs text-muted-foreground mt-2 italic">"{rev.comment}"</p>}
                      </div>
                    );
                  return (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-3" data-testid="portal-review-form">
                      <p className="font-heading font-semibold text-sm">How did we do?</p>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button key={s} data-testid={`review-star-${s}`} onClick={() => setReviewForm({ ...reviewForm, rating: s })} aria-label={`${s} stars`}>
                            <Star className={`w-6 h-6 transition-colors duration-150 ${s <= reviewForm.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground hover:text-amber-400"}`} />
                          </button>
                        ))}
                      </div>
                      <Textarea data-testid="review-comment-input" rows={2} placeholder="Tell us about your crew (optional)"
                        value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} />
                      <Button data-testid="review-submit-button" disabled={!reviewForm.rating || submitting} onClick={() => submitReview(rev)}
                        className="w-full rounded-full bg-amber-400 text-black hover:bg-amber-300 font-semibold">
                        Submit Review
                      </Button>
                    </div>
                  );
                })()}
                <p className="text-right font-heading font-bold text-lg">{fmtMoney(detail.value)}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
